export interface LocaleInformation {
  language: string;
  country: string;
}

export type ApplicationType = 'STANDALONE' | 'FOX';

export const ApplicationTypeKeys: Record<ApplicationType, string> = {
  STANDALONE: 'standalone',
  FOX: 'fox',
} as const;

export function replaceLocale(str: string, language: string, country: string): string {
  let result = str;
  result = result.replace('###LANGUAGE###', language);
  result = result.replace('###COUNTRY###', country);
  return result;
}

export function getDefaultLocale(): LocaleInformation {
  return { language: 'en', country: 'us' };
}

function extractLocaleFromQueryFox(pathname: string): LocaleInformation {
  const pathItems = pathname.split('/');
  const localeInformation = getDefaultLocale();
  let counter = 0;

  if (pathItems.includes('s') && pathItems.includes('shock-absorber')) {
    pathItems.forEach((item: string) => {
      if (item.match(/[a-z]{2}$/g)) {
        if (counter === 0) {
          localeInformation.country = item;
        } else if (counter === 1) {
          localeInformation.language = item;
        }
        counter++;
      }
    });
  }
  return localeInformation;
}

function extractLocaleFromQueryStandalone(hash: string, path: string, routeType: string): LocaleInformation {
  const localeInformation = getDefaultLocale();
  const pathname = routeType === 'hash' && process.env.REACT_APP_ENV === 'development' ? hash.substr(1) : path;
  if (pathname.includes('eac')) {
    const pathItems = pathname.split('/');
    const referenceIndex = pathItems.indexOf('eac');
    const localeItem = pathItems[referenceIndex + 1];

    if (localeItem.match(/^[a-z]{2}_[a-z]{2}$/g)) {
      [localeInformation.language, localeInformation.country] = localeItem.split('_');
    } else if (localeItem.match(/^[a-z]{2}-[a-z]{2}_[a-z]{2}$/g)) {
      [localeInformation.language, localeInformation.country] = localeItem.split('_');
    }
  }

  return localeInformation;
}

export function getRouteType(): string {
  return process.env.REACT_APP_ROUTER_TYPE === 'hash' ? 'hash' : 'default';
}

export function extractLocaleFromQuery(applicationType: string, hash: string, pathname: string): LocaleInformation {
  switch (applicationType) {
    case 'fox': {
      return extractLocaleFromQueryFox(pathname);
    }
    case 'standalone':
    default: {
      return extractLocaleFromQueryStandalone(hash, pathname, getRouteType());
    }
  }
}

function getBackendURLPrefix(backendPrefix: string, language: string, country: string): string {
  let prefix = backendPrefix;

  if (prefix) {
    prefix = replaceLocale(prefix, language, country);
  } else {
    prefix = '';
  }
  return prefix;
}

export function getBaseName(applicationType: string, language: string, country: string): string {
  // Only for development purposes!
  // The only purpose of this code is trying to emulate I-System behavior
  // which is not currently 100% emulated on our local machine
  if (applicationType === ApplicationTypeKeys.STANDALONE && process.env.REACT_APP_ENV === 'development') {
    return `/eac/${language}_${country}/ShockAbsorber`;
  }

  return '';
}

export function getApplicationType(pathname: string, hostname: string): string {
  return pathname.includes('eac') || hostname.includes('localhost')
    ? ApplicationTypeKeys.STANDALONE
    : ApplicationTypeKeys.FOX;
}

export function getAssetPathPrefix(applicationType: string, backendPrefix: string): string {
  let assetPathPrefix =
    process.env.REACT_APP_FOX_PATH_PREFIX !== undefined ? process.env.REACT_APP_FOX_PATH_PREFIX : '';
  if (applicationType === 'fox') {
    assetPathPrefix = backendPrefix + assetPathPrefix;
  }
  return assetPathPrefix;
}

export function getBackendPrefix(country: string, language: string): string {
  return process.env.REACT_APP_BACKEND_URL_PREFIX !== undefined
    ? getBackendURLPrefix(process.env.REACT_APP_BACKEND_URL_PREFIX, country, language)
    : '';
}

/**
 * @param raw If true returns pathname together with the hash's character.
 * Otherwise returns the pathname without the hash's character.
 */
export function getHashPathName(raw: boolean, hash: string): string {
  let pathname = hash;
  if (!raw) {
    pathname = hash.substr(1);
  }
  [pathname] = pathname.split('?');

  return pathname;
}
